/**
 * ProfileSidebar - левый sticky-сайдбар страницы профиля.
 * Аватар (кадрирование перед загрузкой, удаление с подтверждением), имя, роль в цветах бейджа из конструктора ролей,
 * место работы, заполненность профиля по данным сервера и меню якорей разделов.
 */
import React, { useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Chip,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Tooltip,
  Typography
} from '@mui/material';
import {
  BadgeOutlined,
  CameraAltOutlined,
  DeleteOutline,
  DevicesOutlined,
  FaceOutlined,
  HistoryOutlined,
  KeyOutlined,
  MailOutline,
  NotificationsNoneOutlined,
  PersonOutline,
  RestartAlt,
  VerifiedUserOutlined
} from '@mui/icons-material';
import { PROFILE_WIDE, ZERDE_COLORS } from '../constants';
import { Completeness, ProfileRole } from '../profileApi';
import { roleAccentColor } from '../roleBadge';
import { ConfirmDialog } from '../../admin/reports/edit/EditCommon';
import { apiErrorMessage } from '../../admin/reports/edit/editApi';
import AvatarCropDialog from './AvatarCropDialog';

export type ProfileTab =
  | 'profile'
  | 'password'
  | 'personal'
  | 'notifications'
  | 'mail'
  | 'biometrics'
  | 'sessions'
  | 'activity'
  | 'permissions'
  | 'services';

/** Пункт меню; alert - короткая метка невыполненного условия, error - блокирует работу */
export interface SidebarTab {
  key: ProfileTab;
  alert?: string;
  alertTone?: 'warning' | 'error';
}

const TAB_META: Record<ProfileTab, { label: string; icon: React.ReactNode }> = {
  profile: { label: 'Профиль', icon: <PersonOutline sx={{ fontSize: 17 }} /> },
  password: { label: 'Изменение пароля', icon: <KeyOutlined sx={{ fontSize: 17 }} /> },
  personal: { label: 'Персональные данные', icon: <BadgeOutlined sx={{ fontSize: 17 }} /> },
  notifications: { label: 'Настройка уведомлений', icon: <NotificationsNoneOutlined sx={{ fontSize: 17 }} /> },
  mail: { label: 'Почта', icon: <MailOutline sx={{ fontSize: 17 }} /> },
  biometrics: { label: 'Биометрия', icon: <FaceOutlined sx={{ fontSize: 17 }} /> },
  sessions: { label: 'Сессии и устройства', icon: <DevicesOutlined sx={{ fontSize: 17 }} /> },
  activity: { label: 'Моя активность', icon: <HistoryOutlined sx={{ fontSize: 17 }} /> },
  permissions: { label: 'Мои права', icon: <VerifiedUserOutlined sx={{ fontSize: 17 }} /> },
  services: { label: 'Сервисы и кэш', icon: <RestartAlt sx={{ fontSize: 17 }} /> }
};

const ALERT_COLORS = {
  warning: { bgcolor: '#ffedd5', color: '#c2410c', border: '1px solid #fed7aa' },
  error: { bgcolor: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca' }
};

export interface ProfileSidebarProps {
  displayName: string;
  role: ProfileRole;
  locationText: string;
  avatarUrl: string | null;
  completeness: Completeness;
  tabs: SidebarTab[];
  activeTab: ProfileTab;
  onTabClick: (key: ProfileTab) => void;
  /** Сохранение кадрированного фото; ошибку показывает окно кадрирования */
  onAvatarSave: (dataUrl: string) => Promise<void>;
  /** Удаление фото; ошибку показывает окно подтверждения */
  onAvatarDelete: () => Promise<void>;
}

const ProfileSidebar: React.FC<ProfileSidebarProps> = ({
  displayName,
  role,
  locationText,
  avatarUrl,
  completeness,
  tabs,
  activeTab,
  onTabClick,
  onAvatarSave,
  onAvatarDelete
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    // Сброс значения: повторный выбор того же файла снова откроет окно
    e.target.value = '';
    if (file) setCropFile(file);
  };

  const openDelete = () => {
    setDeleteError(null);
    setDeleteOpen(true);
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await onAvatarDelete();
      setDeleteOpen(false);
    } catch (err) {
      setDeleteError(apiErrorMessage(err, 'Не удалось удалить фото'));
    } finally {
      setDeleting(false);
    }
  };

  const complete = completeness.total > 0 && completeness.done >= completeness.total;
  const percent = completeness.total > 0 ? Math.round((completeness.done / completeness.total) * 100) : 100;
  // Рамка колонки - в цвет бейджа роли из конструктора ролей
  const roleColor = roleAccentColor(role.badge);

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        flexShrink: 0,
        top: 15,
        // закрепленная колонка выше экрана (ноутбук 1366x768) прокручивается сама, иначе нижние пункты недоступны
        [PROFILE_WIDE]: { width: '280px', position: 'sticky', maxHeight: 'calc(100vh - 120px)', overflowY: 'auto', scrollbarWidth: 'thin' },
        '& > *': { flexShrink: 0 },
        alignSelf: 'flex-start',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        p: 2.5,
        borderRadius: '20px',
        bgcolor: '#ffffff',
        border: `1.5px solid ${roleColor}`,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
        zIndex: 2
      }}
    >
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/jpeg,image/png,image/webp"
        style={{ display: 'none' }}
      />

      <Box sx={{ position: 'relative', display: 'inline-block' }}>
        <Avatar
          src={avatarUrl || undefined}
          alt={displayName}
          sx={{
            width: 110,
            height: 110,
            border: '3px solid #ffffff',
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.12)',
            bgcolor: avatarUrl ? 'transparent' : '#e0f2fe',
            color: ZERDE_COLORS.primary
          }}
        >
          {!avatarUrl && <PersonOutline sx={{ fontSize: 56 }} />}
        </Avatar>

        <Tooltip title={avatarUrl ? 'Заменить фото' : 'Загрузить фото'} arrow>
          <IconButton
            onClick={() => fileInputRef.current?.click()}
            aria-label={avatarUrl ? 'Заменить фото' : 'Загрузить фото'}
            size="small"
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              bgcolor: '#ffffff',
              border: '1px solid #cbd5e1',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              color: '#475569',
              width: 34,
              height: 34,
              '&:hover': { bgcolor: '#f8fafc', color: ZERDE_COLORS.primary }
            }}
          >
            <CameraAltOutlined sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>

        {avatarUrl && (
          <Tooltip title="Удалить фото" arrow>
            <IconButton
              onClick={openDelete}
              aria-label="Удалить фото"
              size="small"
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                bgcolor: '#ffffff',
                border: '1px solid #fecdd3',
                color: '#e11d48',
                width: 24,
                height: 24,
                '&:hover': { bgcolor: '#ffe4e6' }
              }}
            >
              <DeleteOutline sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ textAlign: 'center', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', lineHeight: 1.25, wordBreak: 'break-word' }}>
          {displayName}
        </Typography>
        <Chip
          label={role.name}
          size="small"
          sx={{
            height: 22,
            maxWidth: '100%',
            fontSize: '0.74rem',
            fontWeight: 600,
            bgcolor: role.badge.bg,
            color: role.badge.text,
            border: `1px solid ${role.badge.border}`
          }}
        />
        <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>
          {locationText}
        </Typography>
      </Box>

      {completeness.total > 0 && (
        <Tooltip
          arrow
          title={complete ? 'Все обязательные данные заполнены' : `Не заполнено: ${completeness.missing.join(', ')}`}
        >
          <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75 }}>
              <Typography variant="caption" sx={{ color: '#475569', fontWeight: 600 }}>
                Профиль заполнен на {completeness.done} из {completeness.total}
              </Typography>
              <Typography variant="caption" sx={{ color: complete ? '#15803d' : '#c2410c', fontWeight: 700 }}>
                {percent}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={percent}
              aria-label={`Профиль заполнен на ${completeness.done} из ${completeness.total}`}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: '#f1f5f9',
                '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: complete ? ZERDE_COLORS.emerald : ZERDE_COLORS.orange }
              }}
            />
          </Box>
        </Tooltip>
      )}

      <Divider sx={{ width: '100%' }} />

      {/* Над разделами (узкая страница) пункты идут сеткой в несколько колонок, в боковой колонке - списком */}
      <Box
        component="nav"
        aria-label="Разделы профиля"
        sx={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))',
          gap: 0.25,
          [PROFILE_WIDE]: { gridTemplateColumns: '1fr' }
        }}
      >
        {tabs.map((tab) => {
          const meta = TAB_META[tab.key];
          const isActive = activeTab === tab.key;
          return (
            <Box
              key={tab.key}
              onClick={() => onTabClick(tab.key)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onTabClick(tab.key);
                }
              }}
              role="button"
              tabIndex={0}
              aria-current={isActive ? 'true' : undefined}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.25,
                py: 0.6,
                minHeight: 32,
                borderRadius: '8px',
                cursor: 'pointer',
                userSelect: 'none',
                transition: 'all 0.2s ease',
                bgcolor: isActive ? 'rgba(21, 112, 239, 0.08)' : 'transparent',
                color: isActive ? ZERDE_COLORS.primary : '#475569',
                fontWeight: isActive ? 600 : 500,
                '&:hover': {
                  bgcolor: isActive ? 'rgba(21, 112, 239, 0.12)' : '#f8fafc',
                  color: isActive ? ZERDE_COLORS.primary : '#1e293b'
                },
                '&:focus-visible': { outline: `2px solid ${ZERDE_COLORS.primary}`, outlineOffset: 1 }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', color: isActive ? ZERDE_COLORS.primary : '#64748b' }}>
                {meta.icon}
              </Box>
              <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.3, fontWeight: 'inherit', color: 'inherit', flexGrow: 1, minWidth: 0 }}>
                {meta.label}
              </Typography>
              {tab.alert && (
                <Chip
                  label={tab.alert}
                  size="small"
                  sx={{ height: 18, flexShrink: 0, fontSize: '0.64rem', fontWeight: 700, '& .MuiChip-label': { px: 0.75 }, ...ALERT_COLORS[tab.alertTone || 'warning'] }}
                />
              )}
            </Box>
          );
        })}
      </Box>

      <AvatarCropDialog file={cropFile} onClose={() => setCropFile(null)} onSave={onAvatarSave} />

      <ConfirmDialog
        open={deleteOpen}
        title="Удалить фото профиля?"
        confirmText="Удалить"
        confirmColor="error"
        loading={deleting}
        maxWidth="xs"
        onConfirm={handleDelete}
        onClose={() => setDeleteOpen(false)}
      >
        <Typography variant="body2" sx={{ color: '#475569' }}>
          Фото исчезнет из профиля и шапки панели. Загрузить новое можно в любой момент.
        </Typography>
        {deleteError && <Alert severity="error" sx={{ mt: 2, borderRadius: '10px' }}>{deleteError}</Alert>}
      </ConfirmDialog>
    </Paper>
  );
};

export default ProfileSidebar;
